/**
 * HoleSchematic — the telescoping well schematic for W2. Hand-SVG (no Plotly),
 * scaled by MD depth on Y and by hole diameter on X, so each section reads as a
 * narrower, deeper tube than the one above it (the classic casing telescope).
 * Sections whose top MD sits below the build point deviate to the right to
 * evoke a curve → lateral for horizontal wells; vertical wells stay centred.
 *
 * Clicking a section (or its legend row) selects it, mirroring the grid ↔
 * schematic highlight from the wireframe. Rendered inside the shared ChartFrame
 * so it owns the responsive viewBox + depth gridlines from one place.
 */
import { ChartFrame } from "@components/chart/ChartFrame";
import type { HolePlanRow } from "@api/types";
import { EVENT_LETTER_OP_VAR, type EventLetter } from "@lib/eventEnrichment";

/** A section resolved to drawable geometry (depth range + diameter + colour). */
export interface SchematicSection {
  key: string;
  label: string;
  diameter: number | null;
  topMd: number;
  baseMd: number;
  opVar: string;
}

interface HoleSchematicProps {
  rows: HolePlanRow[];
  /** rowKey(row, index) → the stable id used for selection (matches the grid). */
  rowKey: (row: HolePlanRow, index: number) => string;
  selectedKey: string | null;
  onSelect: (key: string) => void;
  /** Event letter → colours the sections by operation (drilling vs unconv). */
  letter: EventLetter | null;
  /** Below this MD, sections lean right (build → lateral). null = fully vertical. */
  buildMd: number | null;
  caption: string;
  ariaLabel: string;
}

const WIDTH = 260;
const HEIGHT = 440;
const PAD = { top: 20, right: 40, bottom: 16, left: 20 };

/** Half-width (px) of a section tube for a given diameter, at the deepest scale. */
function halfWidthFor(diameter: number | null, maxDiameter: number): number {
  const d = diameter && diameter > 0 ? diameter : 1;
  // Map the largest diameter to ~46px half-width, the smallest to ~14px.
  const t = maxDiameter > 0 ? d / maxDiameter : 1;
  return 14 + t * 32;
}

export function HoleSchematic({
  rows,
  rowKey,
  selectedKey,
  onSelect,
  letter,
  buildMd,
  caption,
  ariaLabel,
}: HoleSchematicProps) {
  const sections: SchematicSection[] = rows.map((r, i) => ({
    key: rowKey(r, i),
    label: r.section || r.phase || `#${i + 1}`,
    diameter: r.hole_diameter,
    topMd: r.top_md ?? 0,
    baseMd: r.base_md ?? r.top_md ?? 0,
    opVar: letter ? EVENT_LETTER_OP_VAR[letter] : "--op-drilling",
  }));

  const maxMd = Math.max(
    1,
    ...sections.map((s) => s.baseMd),
    ...sections.map((s) => s.topMd),
  );
  const maxDiameter = Math.max(1, ...sections.map((s) => s.diameter ?? 0));

  // Depth gridlines every ~1000 m (at least one, capped so labels stay legible).
  const gridStep = maxMd > 6000 ? 2000 : 1000;
  const gridMarks: number[] = [];
  for (let d = gridStep; d < maxMd; d += gridStep) gridMarks.push(d);

  return (
    <div className="hole-schem">
      <div className="hole-schem-cap">
        <span>{caption}</span>
        <span className="tab-nums">0–{Math.round(maxMd).toLocaleString()} m</span>
      </div>
      <ChartFrame
        width={WIDTH}
        height={HEIGHT}
        padding={PAD}
        xDomain={[-1, 1]}
        yDomain={[0, maxMd]}
        yDown
        ariaLabel={ariaLabel}
      >
        {({ plot, sx, sy }) => {
          const cx = sx(0); // centreline x
          const deviateFrom = buildMd; // MD at which sections start leaning right

          return (
            <g>
              {/* depth gridlines + labels */}
              {gridMarks.map((d) => (
                <g key={d}>
                  <line
                    x1={plot.x}
                    y1={sy(d)}
                    x2={plot.x + plot.w}
                    y2={sy(d)}
                    stroke="var(--line)"
                    strokeWidth={0.5}
                    strokeDasharray="2 3"
                    opacity={0.55}
                  />
                  <text
                    x={plot.x + plot.w + 4}
                    y={sy(d) + 3}
                    fontSize={9}
                    fill="var(--dim)"
                    className="tab-nums"
                  >
                    {d.toLocaleString()}
                  </text>
                </g>
              ))}
              {/* ground line */}
              <line
                x1={plot.x}
                y1={sy(0)}
                x2={plot.x + plot.w}
                y2={sy(0)}
                stroke="var(--line)"
                strokeWidth={1.5}
              />
              <text x={plot.x + plot.w + 4} y={sy(0) + 3} fontSize={9} fill="var(--dim)">
                0 m
              </text>
              {/* centreline */}
              <line
                x1={cx}
                y1={sy(0)}
                x2={cx}
                y2={sy(maxMd)}
                stroke="var(--line)"
                strokeWidth={1}
                opacity={0.5}
              />

              {/* sections, deepest drawn first so shallow (wider) tubes sit on top */}
              {[...sections].reverse().map((s) => {
                const isSel = s.key === selectedKey;
                const hw = halfWidthFor(s.diameter, maxDiameter);
                const yTop = sy(s.topMd);
                const yBase = sy(s.baseMd);
                // Horizontal lean: sections below the build MD walk to the right.
                const lean =
                  deviateFrom != null && s.topMd >= deviateFrom
                    ? Math.min(plot.w * 0.42, ((s.baseMd - deviateFrom) / maxMd) * plot.w * 1.6)
                    : 0;
                const topLean =
                  deviateFrom != null && s.topMd >= deviateFrom
                    ? Math.min(plot.w * 0.42, ((s.topMd - deviateFrom) / maxMd) * plot.w * 1.6)
                    : 0;
                const fill = `var(${s.opVar})`;
                // A simple trapezoid so a leaning section reads as a deviated tube.
                const d = `M ${cx - hw + topLean} ${yTop}
                           L ${cx + hw + topLean} ${yTop}
                           L ${cx + hw + lean} ${yBase}
                           L ${cx - hw + lean} ${yBase} Z`;
                return (
                  <g
                    key={s.key}
                    className="hole-schem-section"
                    role="button"
                    tabIndex={0}
                    aria-label={s.label}
                    aria-pressed={isSel}
                    onClick={() => onSelect(s.key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelect(s.key);
                      }
                    }}
                  >
                    <path
                      d={d}
                      fill={fill}
                      fillOpacity={isSel ? 0.95 : 0.72}
                      stroke={isSel ? "var(--head)" : "var(--surface)"}
                      strokeWidth={isSel ? 1.6 : 1}
                    />
                    {/* casing shoe at the section base */}
                    <line
                      x1={cx - hw - 4 + lean}
                      y1={yBase}
                      x2={cx + hw + 4 + lean}
                      y2={yBase}
                      stroke="var(--head)"
                      strokeWidth={isSel ? 2 : 1.5}
                      opacity={0.8}
                    />
                  </g>
                );
              })}

              {/* TD marker at the deepest base */}
              <text
                x={cx}
                y={sy(maxMd) - 6}
                fontSize={9}
                fill="var(--dim)"
                textAnchor="middle"
              >
                TD {Math.round(maxMd).toLocaleString()} m
              </text>
            </g>
          );
        }}
      </ChartFrame>
    </div>
  );
}
